"use client";

import { useI18n } from "@/i18n";
import { useLangHref } from "@/i18n/useLangHref";

const LAST_UPDATED = "September 2026";

const styles = {
    page: {
        minHeight: "100vh",
        background: "#F5F7F5",
        paddingTop: 96,
        paddingBottom: 80,
    },
    container: {
        maxWidth: 820,
        margin: "0 auto",
        padding: "0 24px",
    },
    card: {
        background: "#ffffff",
        borderRadius: 16,
        border: "1px solid #ECEEEC",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        padding: "48px 56px",
    },
    title: {
        fontSize: 32,
        fontWeight: 800,
        color: "#111827",
        margin: 0,
        letterSpacing: "-0.01em",
    },
    subtitle: {
        fontSize: 14,
        color: "#6B7280",
        marginTop: 10,
        marginBottom: 28,
        paddingBottom: 24,
        borderBottom: "1px solid #F1F3F1",
    },
    hero: {
        background: "#F0FDF4",
        border: "1px solid #BBF7D0",
        borderRadius: 14,
        padding: "18px 20px",
        fontSize: 14.5,
        lineHeight: 1.7,
        color: "#166534",
        fontWeight: 500,
        marginTop: 4,
        marginBottom: 8,
    },
    intro: {
        fontSize: 15,
        lineHeight: 1.8,
        color: "#374151",
        marginTop: 8,
    },
    categoryBar: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        marginTop: 28,
        marginBottom: 24,
    },
    catBtn: {
        padding: "8px 16px",
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 600,
        border: "1px solid #E5E7EB",
        background: "#ffffff",
        color: "#374151",
        cursor: "pointer",
        transition: "all 0.18s ease",
    },
    section: {
        marginTop: 32,
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: 700,
        color: "#111827",
        margin: "0 0 16px",
    },
    faq: {
        border: "1px solid #ECEEEC",
        borderRadius: 12,
        marginBottom: 10,
        overflow: "hidden",
        background: "#ffffff",
    },
    faqHead: {
        padding: "16px 20px",
        fontSize: 14.5,
        fontWeight: 600,
        color: "#111827",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 12,
        userSelect: "none",
    },
    faqCat: {
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 6,
        background: "#F4F6F4",
        color: "#374151",
        flexShrink: 0,
    },
    faqBody: {
        padding: "0 20px 16px",
        fontSize: 14,
        lineHeight: 1.75,
        color: "#4B5563",
    },
    contactBox: {
        marginTop: 32,
        padding: "20px 24px",
        background: "#F4F6F4",
        borderRadius: 12,
        fontSize: 14,
        color: "#374151",
        lineHeight: 1.8,
    },
};

export default function HelpPage() {
    const { t } = useI18n();
    const langHref = useLangHref();

    const catKeys = ["account", "listing", "search", "promotion", "transaction"];

    const renderFaqItem = (q, a) => (
        <details key={q} style={styles.faq}>
            <summary style={styles.faqHead}>
                <span style={{ flex: 1 }}>{q}</span>
                <span style={{ color: "#9CA3AF", fontSize: 18 }}>+</span>
            </summary>
            <div style={styles.faqBody}>{a}</div>
        </details>
    );

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <article style={styles.card}>
                    <h1 style={styles.title}>{t("help.title")}</h1>
                    <div style={styles.subtitle}>
                        {t("meta.siteName")} · {t("help.subtitle")}
                    </div>

                    <div style={styles.hero}>
                        {t("help.contactCta")}{" "}
                        <a
                            href={langHref("/contact")}
                            style={{ color: "#166534", fontWeight: 700, margin: "0 4px" }}
                        >
                            {t("help.contactLinkLabel")}
                        </a>
                        .
                    </div>

                    {/* Category quick nav */}
                    <div style={styles.categoryBar}>
                        <a key="all" href="#cat-all" style={styles.catBtn}>
                            {t("help.categories.all")}
                        </a>
                        {catKeys.map((k) => (
                            <a key={k} href={`#cat-${k}`} style={styles.catBtn}>
                                {t(`help.categories.${k}`)}
                            </a>
                        ))}
                    </div>

                    {/* FAQ by category */}
                    {catKeys.map((cat) => {
                        const items = t(`help.faqs.${cat}`);
                        if (!Array.isArray(items) || items.length === 0) return null;
                        return (
                            <section key={cat} id={`cat-${cat}`} style={styles.section}>
                                <h2 style={styles.sectionTitle}>{t(`help.categories.${cat}`)}</h2>
                                {items.map((f, i) =>
                                    renderFaqItem(
                                        typeof f === "string" ? f : f?.q || f?.question || "",
                                        typeof f === "string" ? "" : f?.a || f?.answer || ""
                                    )
                                )}
                            </section>
                        );
                    })}

                    <div style={styles.contactBox}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("help.stillHaveQuestions")}</div>
                        <div>{t("help.contactBoxLine1")}</div>
                        <div style={{ marginTop: 8 }}>
                            {t("help.contactBoxEmailLabel")}：
                            <a href="mailto:18918855336@126.com" style={{ color: "#2F9E44" }}>
                                18918855336@126.com
                            </a>
                            {" "}{t("help.contactBoxOr")}{" "}
                            <a
                                href={langHref("/contact")}
                                style={{ color: "#2F9E44", fontWeight: 600 }}
                            >
                                {t("help.contactPage")}
                            </a>
                        </div>
                    </div>
                </article>
            </div>
        </div>
    );
}
